import { useMemo } from "react";
import { Eye, MousePointerClick, Percent, Globe } from "lucide-react";
import { HeadlineTiles, WhoSawIt, BestAds, WhereAndWhat, GenderSplit, rankAds, fmt } from "../story/StoryParts";
import { audienceGroups, audienceStory, deviceRows, deviceGapNote } from "../story/storyData";
import { META_BENCHMARK_CTR, metaHeadline, metaDevices, metaVisits, metaAudienceItems, metaAdList, metaPlacementRows } from "./metaData";
import TrendsPanel from "../story/TrendsPanel";

export default function MetaStoryLayout({ clientName, metaSummary, deviceMetrics, facebookCampaign, fbAds, CreativeIcon, clientId, API, readOnly, reportStart, reportEnd }) {
  const d = useMemo(() => {
    const head = metaHeadline(metaSummary, fbAds, facebookCampaign);
    const dev = deviceRows(metaDevices(deviceMetrics));
    return {
      head, dev,
      visits: metaVisits(facebookCampaign),
      aud: audienceGroups(metaAudienceItems(facebookCampaign, metaSummary)),
      ads: rankAds(metaAdList(facebookCampaign, fbAds)),
      placements: metaPlacementRows(metaSummary),
    };
  }, [metaSummary, deviceMetrics, facebookCampaign, fbAds]);

  if (!d.head.impressions) return null;
  const tiles = [
    { icon: Eye, label: "Times seen", value: fmt(d.head.impressions), testId: "meta-kpi-impressions" },
    { icon: MousePointerClick, label: "Clicks", value: fmt(d.head.clicks), sub: "People who tapped to learn more", testId: "meta-kpi-clicks" },
    { icon: Percent, label: "Click rate", value: `${d.head.ctr.toFixed(2)}%`, sub: "Clicks per ad seen", testId: "meta-kpi-ctr" },
  ];
  if (d.visits.view > 0) {
    tiles.push({
      icon: Globe, label: "Bonus visits, no click", value: `+${fmt(d.visits.view)}`, testId: "meta-kpi-visits",
      sub: `Saw an ad, didn't click, then came to your website on their own. On top of the ${fmt(d.head.clicks)} clicks.${d.visits.viewRows.length ? ` ${d.visits.viewRows.slice(0, 2).map((r) => `${r.name.replace(/visits?|page ?view/gi, "").trim()} ${r.count}`).join(" · ")}` : ""}`,
    });
  }
  return (
    <div data-testid="meta-story-layout">
      <HeadlineTiles tiles={tiles} gauge={{ ctr: d.head.ctr, benchmark: META_BENCHMARK_CTR, label: "vs typical FB & IG ad", testId: "meta-benchmark-gauge" }} />
      <TrendsPanel clientId={clientId} clientName={clientName} API={API} readOnly={readOnly} reportStart={reportStart} reportEnd={reportEnd}
        endpoint="meta-trends" channel="meta" benchmark={META_BENCHMARK_CTR} benchmarkLabel="Typical FB & IG click rate" channelLabel="Facebook and Instagram" typicalLabel="Facebook and Instagram ad" p="meta" showYearInReview showHighlights={false} />
      {d.ads.length > 0 && <BestAds ads={d.ads} CreativeIcon={CreativeIcon} campaignType="facebook" benchmark={META_BENCHMARK_CTR} testPrefix="meta" />}
      {d.aud && <WhoSawIt groups={d.aud.groups} back={d.aud.back} fresh={d.aud.fresh} story={audienceStory(d.aud.back, d.aud.fresh)} testPrefix="meta" />}
      <WhereAndWhat testId="meta-where-what" donut sections={[
        { title: "Where your ads showed up", rows: d.placements, testId: "meta-placements" },
        { title: "What people saw them on", rows: d.dev.rows, note: deviceGapNote(d.head.impressions, d.dev.total), testId: "meta-devices" },
      ]} />
      <GenderSplit gender={metaSummary?.gender || []} testId="meta-gender" />
    </div>
  );
}
